<script lang="ts">
  /**
   * Passkey management for the one account there is.
   *
   * This exists because of a specific dead end. A passkey created by Windows Hello, or by any
   * security key, is bound to that hardware: it is not in a password manager, it does not sync,
   * and no amount of signing in on a phone will make it appear there. With one such key and no
   * way to add a second, the logbook is reachable from exactly one machine forever, and losing
   * that machine means editing SQLite by hand.
   *
   * The way out is already in the protocol and only needed a button. Registration is not pinned
   * to `platform`, so "Passkey hinzufügen" lets the browser offer *another* device: the desktop
   * shows a QR code, the phone scans it, and the new credential is created in the phone's own
   * password manager. The phone then signs in on its own, with nothing borrowed from here.
   */
  import { startRegistration } from '@simplewebauthn/browser';
  import { api } from './api.ts';
  import type { Passkey } from './api.ts';
  import { localDate } from './format.ts';

  interface Props {
    username: string;
    /** Called after a sign-out, so the shell can put the gate back. */
    onSignedOut: () => void;
  }

  let { username, onSignedOut }: Props = $props();

  let open = $state(false);
  let busy = $state(false);
  let error = $state<string | null>(null);
  let keys = $state<Passkey[]>([]);
  let label = $state('');

  async function load(): Promise<void> {
    try {
      keys = await api.credentials();
    } catch (err) {
      error = message(err);
    }
  }

  $effect(() => {
    if (open) void load();
  });

  async function add(): Promise<void> {
    busy = true;
    error = null;
    try {
      const { flowId, options } = await api.credentialsOptions();
      const response = await startRegistration({ optionsJSON: options });
      await api.credentialsVerify(flowId, response, label.trim() === '' ? null : label.trim());
      label = '';
      await load();
    } catch (err) {
      error = message(err);
    } finally {
      busy = false;
    }
  }

  async function remove(id: string): Promise<void> {
    busy = true;
    error = null;
    try {
      await api.removeCredential(id);
      await load();
    } catch (err) {
      error = message(err);
    } finally {
      busy = false;
    }
  }

  async function signOut(): Promise<void> {
    busy = true;
    try {
      await api.logout();
      onSignedOut();
    } catch (err) {
      error = message(err);
    } finally {
      busy = false;
    }
  }

  /** Cancelling the system sheet throws `NotAllowedError`; a deliberate "not now" is not an error. */
  function message(err: unknown): string | null {
    if (err instanceof Error && err.name === 'NotAllowedError') return null;
    return err instanceof Error ? err.message : String(err);
  }

  /**
   * True when every key on the account is hardware-bound.
   *
   * Worth saying out loud rather than leaving to be discovered on a station platform: in that
   * state there is no second device and no recovery, and the fix has to happen from the machine
   * that still works.
   */
  const strandable = $derived(keys.length > 0 && keys.every((k) => !k.backedUp));
</script>

<div class="account">
  <button class="who" type="button" onclick={() => (open = !open)} aria-expanded={open}>
    <span class="dot" aria-hidden="true"></span>
    {username}
  </button>

  {#if open}
    <div class="panel">
      <p class="lead">
        Um das Flugbuch <strong>auf dem Handy</strong> zu benutzen, hier einen zweiten Passkey
        anlegen und im Browser-Dialog <em>„Anderes Gerät“</em> bzw. <em>„Telefon“</em> wählen. Der
        QR-Code, der dann erscheint, legt den Passkey auf dem Handy an — nicht hier.
      </p>

      <div class="add">
        <input bind:value={label} placeholder="Bezeichnung, z. B. Handy" maxlength="40" />
        <button class="primary" type="button" onclick={() => void add()} disabled={busy}>
          {busy ? '…' : 'Passkey hinzufügen'}
        </button>
      </div>

      {#if strandable}
        <p class="warn">
          Alle vorhandenen Passkeys sind an ein Gerät gebunden und werden nirgendwo gesichert.
          Geht dieses Gerät verloren, kommt niemand mehr ins Flugbuch.
        </p>
      {/if}

      <ul>
        {#each keys as key (key.id)}
          <li>
            <span class="name">
              {key.label ?? 'Passkey'}
              {#if !key.backedUp}<span class="bound" title="Nur auf diesem Gerät, wird nicht gesichert">
                  nur hier</span
                >{/if}
            </span>
            <span class="when">seit {localDate(key.createdAt)}</span>
            <button
              class="remove"
              type="button"
              onclick={() => void remove(key.id)}
              disabled={busy || keys.length <= 1}
              title={keys.length <= 1 ? 'Der letzte Passkey kann nicht entfernt werden' : 'Entfernen'}
            >
              entfernen
            </button>
          </li>
        {/each}
      </ul>

      {#if error !== null}<p class="bad">{error}</p>{/if}

      <button class="out" type="button" onclick={() => void signOut()} disabled={busy}>
        Abmelden
      </button>
    </div>
  {/if}
</div>

<style>
  .account {
    position: relative;
    align-self: flex-start;
  }

  .who {
    display: flex;
    align-items: center;
    gap: 0.4rem;
    border: 1px solid var(--line);
    background: var(--surface-2, #161a22);
    color: var(--text-dim);
    font: inherit;
    font-size: 0.8rem;
    padding: 0.3rem 0.75rem;
    border-radius: 999px;
    cursor: pointer;
  }
  .dot {
    width: 0.45rem;
    height: 0.45rem;
    border-radius: 50%;
    background: var(--accent);
  }

  .panel {
    position: absolute;
    z-index: 20;
    top: calc(100% + 0.4rem);
    left: 0;
    width: min(24rem, calc(100vw - 2rem));
    display: flex;
    flex-direction: column;
    gap: 0.6rem;
    padding: 0.85rem;
    border: 1px solid var(--line);
    border-radius: 0.7rem;
    background: var(--bg-raised);
    box-shadow: 0 1rem 2rem rgb(0 0 0 / 0.45);
  }

  /*
   * On a phone the panel is a sheet across the screen, not a dropdown hanging off the chip.
   *
   * Anchored to the chip it did not fit: the chip starts about 150 px in, the panel is up to
   * 358 px wide, and on a 390 px screen everything past the halfway mark — the label field and
   * the "Passkey hinzufügen" button — was off the right edge, reachable only by scrolling the
   * whole page sideways. Which made the one control that gets a passkey *onto* a phone the one
   * control a phone could not reach.
   *
   * Anchoring right instead only moves the problem to the other edge. `position: fixed` with
   * both insets set and `top: auto` keeps the vertical position it would have had — directly
   * under the chip — while measuring left and right against the viewport rather than the chip.
   */
  @media (max-width: 700px) {
    .panel {
      position: fixed;
      top: auto;
      left: 0.7rem;
      right: 0.7rem;
      width: auto;
    }
  }

  .lead {
    margin: 0;
    font-size: 0.78rem;
    line-height: 1.55;
    color: var(--text-dim);
  }

  .add {
    display: flex;
    gap: 0.4rem;
  }
  input {
    flex: 1;
    min-width: 0;
    font: inherit;
    font-size: 0.82rem;
    color: var(--text);
    background: var(--surface-2, #161a22);
    border: 1px solid var(--line);
    border-radius: 0.45rem;
    padding: 0.4rem 0.55rem;
  }

  .primary {
    font: inherit;
    font-size: 0.8rem;
    font-weight: 600;
    color: #04121a;
    background: var(--accent);
    border: 0;
    border-radius: 999px;
    padding: 0.4rem 0.85rem;
    cursor: pointer;
    white-space: nowrap;
  }
  .primary:disabled {
    opacity: 0.55;
    cursor: default;
  }

  .warn {
    margin: 0;
    font-size: 0.75rem;
    line-height: 1.5;
    color: var(--warn, #e0a33a);
  }

  ul {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 0.3rem;
  }
  li {
    display: flex;
    align-items: baseline;
    gap: 0.5rem;
    font-size: 0.78rem;
  }
  .name {
    font-weight: 600;
  }
  .bound {
    margin-left: 0.3rem;
    font-weight: 400;
    font-size: 0.72rem;
    color: var(--warn, #e0a33a);
  }
  .when {
    color: var(--text-faint);
    margin-left: auto;
    white-space: nowrap;
  }
  .remove {
    border: 0;
    background: transparent;
    color: var(--bad, #e5544b);
    font: inherit;
    font-size: 0.75rem;
    cursor: pointer;
    padding: 0;
  }
  .remove:disabled {
    color: var(--text-faint);
    cursor: default;
  }

  .out {
    align-self: flex-start;
    border: 1px solid var(--line);
    background: transparent;
    color: var(--text-dim);
    font: inherit;
    font-size: 0.78rem;
    padding: 0.3rem 0.75rem;
    border-radius: 999px;
    cursor: pointer;
  }

  .bad {
    margin: 0;
    font-size: 0.78rem;
    line-height: 1.5;
    color: var(--bad, #e5544b);
  }
</style>
